import i18n from '../../../i18n';
import { currentLocale, type AppLocale } from '../../../i18n/locale';
import type { Graph, GraphWrite } from '../../../lib/api';

/**
 * The quick-automation "recipe" — the small, flat state the guided builder
 * edits, plus the compiler that turns it into a normal automation graph.
 *
 * The recipe is deliberately **not** a second automation model. It is a form
 * shape that compiles to the same nodes the canvas builder produces, so a
 * quick automation is indistinguishable from a hand-built one once saved
 * (D-079) and stays editable in the builder afterwards.
 *
 * Three kinds (D-102), modelled on ManyChat's Quick Automations: a comment on a
 * post, a DM, a story reply — each answered by one DM carrying the link.
 */

export type QuickKind = 'comment' | 'dm' | 'story';
export type PostScope = 'specific_post' | 'any_post';
export type StoryScope = 'specific_story' | 'any_story';
/** `any_word`: every comment / message / story reply starts the automation. */
export type MatchMode = 'specific_words' | 'any_word';

export interface LinkButton {
  label: string;
  url: string;
}

export interface QuickRecipe {
  kind: QuickKind;
  /** Which connected account the automation listens on; null = any channel. */
  channelAccountId: string | null;
  channelCode: string | null;
  /** Comment kind only. */
  postScope: PostScope;
  postId: string | null;
  /**
   * Comment kind only: a post ChatFlow has scheduled but not published yet
   * (D-103). Takes the place of `postId`, which does not exist until then.
   */
  scheduledPostId: string | null;
  /** Story kind only (D-117): one of the stories currently up, or any story. */
  storyScope: StoryScope;
  storyId: string | null;
  /** The words a comment, message or story reply must contain. */
  match: MatchMode;
  keywords: string[];
  /** Comment kind only. */
  publicReply: { enabled: boolean; text: string };
  /**
   * The single DM the contact receives, link button included (D-083). It is
   * one message and not two because only the first one can be delivered — see
   * `compileRecipe`.
   */
  dm: { text: string; button: LinkButton | null };
  /** DM and story kinds: a ❤️ on the contact's message before the reply (D-105). */
  react: boolean;
  /**
   * DM and story kinds: only followers get the link; everyone else gets
   * `askText` and sends their message again once they follow (D-106).
   */
  followGate: { enabled: boolean; askText: string };
}

export type QuickSection = 'post' | 'comment' | 'message' | 'story' | 'reply' | 'dm';

/** Sections per kind, in the order they read as a sentence. */
export const KIND_SECTIONS: Record<QuickKind, readonly QuickSection[]> = {
  comment: ['post', 'comment', 'dm'],
  dm: ['message', 'dm'],
  story: ['story', 'reply', 'dm'],
};

/**
 * Defaults ship with usable copy rather than empty fields: the point of the
 * guided builder is that someone can go live without writing anything, then
 * edit. The button is left empty, because a placeholder URL would be a lie we
 * would then publish.
 *
 * These are the contact's messages, not UI chrome: they carry emoji and the
 * `{{first_name|…}}` variable the engine fills in, so they live here, one set
 * per language, rather than in the catalogues (which refuse both).
 */
const DEFAULT_COPY: Record<AppLocale, { dm: Record<QuickKind, string>; publicReply: string; ask: Record<'story' | 'other', string> }> = {
  en: {
    dm: {
      comment: "Hey {{first_name|there}} 👋 Thanks so much for commenting — here's the link ✨",
      dm: "Hey {{first_name|there}} 👋 Thanks for your message — here's the link ✨",
      story: "Thanks for replying to my story, {{first_name|there}}! Here's the link ✨",
    },
    publicReply: 'Sent you a DM {{first_name|there}} 💌',
    ask: {
      story: 'Follow me first, then reply to my story again and the link is yours ✨',
      other: 'Follow me first, then message me again and the link is yours ✨',
    },
  },
  fr: {
    dm: {
      comment: 'Bonjour {{first_name|à vous}} 👋 Merci pour votre commentaire, voici le lien ✨',
      dm: 'Bonjour {{first_name|à vous}} 👋 Merci pour votre message, voici le lien ✨',
      story: 'Merci d’avoir répondu à ma story, {{first_name|à vous}} ! Voici le lien ✨',
    },
    publicReply: 'Je vous ai envoyé un DM 💌',
    ask: {
      story: 'Abonnez-vous d’abord, puis répondez à nouveau à ma story : le lien est à vous ✨',
      other: 'Abonnez-vous d’abord, puis écrivez-moi à nouveau : le lien est à vous ✨',
    },
  },
};

export function emptyRecipe(kind: QuickKind = 'comment', locale: AppLocale = currentLocale()): QuickRecipe {
  const copy = DEFAULT_COPY[locale];
  return {
    kind,
    channelAccountId: null,
    channelCode: null,
    postScope: 'specific_post',
    postId: null,
    scheduledPostId: null,
    // Stories expire within a day, so "any story" is the choice that keeps
    // working; pinning one is the deliberate narrowing (D-117).
    storyScope: 'any_story',
    storyId: null,
    // A DM automation answers keywords by default: answering *every* message
    // is what "Respond to all your DMs" in the template gallery is for.
    match: kind === 'dm' ? 'specific_words' : 'any_word',
    keywords: [],
    publicReply: { enabled: false, text: copy.publicReply },
    dm: { text: copy.dm[kind], button: null },
    react: false,
    followGate: {
      enabled: false,
      askText: copy.ask[kind === 'story' ? 'story' : 'other'],
    },
  };
}

export const MAX_DM_LENGTH = 1000;
export const MAX_BUTTON_LABEL = 40;
/** The server's keyword list for triggers (`keywordList` in node-types.ts): more is refused at save. */
export const MAX_KEYWORDS = 20;
export const MAX_KEYWORD_LENGTH = 80;
/** Instagram's comment length cap — the server's `reply_comment` limit. */
export const MAX_PUBLIC_REPLY_LENGTH = 2200;

const t = i18n.t.bind(i18n);

/** What is wrong with the words to look for in the comment, message or reply. */
export function keywordProblems(match: MatchMode, keywords: readonly string[], what: 'comment' | 'message' | 'reply'): string[] {
  if (match !== 'specific_words') return [];
  if (keywords.length === 0) return [t(`quick.problems.words.${what}`)];
  const problems: string[] = [];
  if (keywords.length > MAX_KEYWORDS) problems.push(t('quick.problems.tooManyWords', { max: MAX_KEYWORDS }));
  if (keywords.some((word) => word.length > MAX_KEYWORD_LENGTH)) problems.push(t('quick.problems.wordTooLong', { max: MAX_KEYWORD_LENGTH }));
  return problems;
}

/** What is wrong with the public reply under the comment. */
export function publicReplyProblems(reply: { enabled: boolean; text: string }): string[] {
  if (!reply.enabled) return [];
  if (!reply.text.trim()) return [t('quick.problems.publicReplyEmpty')];
  return reply.text.length > MAX_PUBLIC_REPLY_LENGTH ? [t('quick.problems.publicReplyLong', { max: MAX_PUBLIC_REPLY_LENGTH })] : [];
}

/** What is wrong with the message for people who do not follow yet — a DM, with a DM's limit. */
export function askTextProblems(gate: { enabled: boolean; askText: string }, orTurnOff = false): string[] {
  if (!gate.enabled) return [];
  if (!gate.askText.trim()) return [t(orTurnOff ? 'quick.problems.askEmptyOrOff' : 'quick.problems.askEmpty')];
  return gate.askText.length > MAX_DM_LENGTH ? [t('quick.problems.askLong', { max: MAX_DM_LENGTH })] : [];
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Per-section blockers, in the same order as the form. The server validates
 * the compiled graph again at save and at publish — these exist so the wizard
 * can name the missing piece next to the field that owns it.
 */
export function sectionProblems(recipe: QuickRecipe): Partial<Record<QuickSection, string[]>> {
  const words = (what: 'comment' | 'message' | 'reply') => keywordProblems(recipe.match, recipe.keywords, what);

  // Every quick kind is "send a link" (D-102), and its default DM says "voici le lien":
  // going live without the button would promise a link it never sends.
  const dm = [...dmProblems(recipe.dm), ...(recipe.dm.button ? [] : [t('quick.problems.linkMissing')])];

  switch (recipe.kind) {
    case 'comment': {
      const post =
        recipe.postScope === 'specific_post' && !recipe.postId && !recipe.scheduledPostId
          ? [t('quick.problems.post')]
          : [];
      const comment = [...words('comment'), ...publicReplyProblems(recipe.publicReply)];
      return { post, comment, dm };
    }
    case 'dm':
    case 'story': {
      const withGate = [...dm, ...askTextProblems(recipe.followGate, true)];
      const story = recipe.storyScope === 'specific_story' && !recipe.storyId ? [t('quick.problems.story')] : [];
      return recipe.kind === 'dm'
        ? { message: words('message'), dm: withGate }
        : { story, reply: words('reply'), dm: withGate };
    }
  }
}

/** What is wrong with a DM and its optional link button. */
export function dmProblems(dm: QuickRecipe['dm']): string[] {
  const problems: string[] = [];
  if (!dm.text.trim()) problems.push(t('quick.problems.dmEmpty'));
  if (dm.text.length > MAX_DM_LENGTH) problems.push(t('quick.problems.dmLong', { max: MAX_DM_LENGTH }));
  const button = dm.button;
  if (button) {
    if (!button.label.trim()) problems.push(t('quick.problems.buttonLabel'));
    if (button.label.length > MAX_BUTTON_LABEL) problems.push(t('quick.problems.buttonLong', { max: MAX_BUTTON_LABEL }));
    if (!isHttpUrl(button.url.trim())) problems.push(t('quick.problems.buttonUrl'));
  }
  return problems;
}

export function recipeProblems(recipe: QuickRecipe): string[] {
  const bySection = sectionProblems(recipe);
  return KIND_SECTIONS[recipe.kind].flatMap((section) => bySection[section] ?? []);
}

/**
 * Auto-generated name; the builder's name field is the place to change it.
 * Once saved it is the user's data (GLOSSARY-FR §1), so it is not translated.
 */
export function recipeName(recipe: Pick<QuickRecipe, 'kind' | 'keywords'>, postCaption: string | null = null): string {
  if (recipe.kind === 'dm') return recipe.keywords.length > 0 ? `DM “${recipe.keywords[0]}” → link` : 'DM → link';
  if (recipe.kind === 'story') return 'Story reply → link';
  const caption = postCaption?.trim().replace(/\s+/g, ' ') ?? '';
  if (!caption) return 'Comment → DM';
  return `Comment → DM · ${caption.length > 40 ? `${caption.slice(0, 40)}…` : caption}`;
}

/**
 * What the trigger watches: for a comment a scheduled (D-103) or published
 * post, for a story reply one of the stories up (D-117), or anything.
 */
function triggerPin(recipe: QuickRecipe): Record<string, string> {
  if (recipe.kind === 'comment' && recipe.postScope === 'specific_post') {
    if (recipe.scheduledPostId) return { scheduledPostId: recipe.scheduledPostId };
    return recipe.postId ? { postId: recipe.postId } : {};
  }
  if (recipe.kind === 'story' && recipe.storyScope === 'specific_story' && recipe.storyId) return { storyId: recipe.storyId };
  return {};
}

/** Vertical spacing between the compiled nodes, matching the templates' layout. */
const NODE_GAP = 160;

/**
 * Compiles the recipe into a graph the engine already knows how to run.
 *
 * **One DM, carrying the link** (D-083). ManyChat sends an opening DM and gates
 * the link behind a tap on it; the engine has no postback-resume path yet, so
 * both would go out back to back (D-079) — and for a comment, the second one
 * is undeliverable anyway: Meta accepts an unsolicited DM only inside the 24h
 * window opened by the contact's own last message, and a commenter has almost
 * never written to the account. The opening message gets through as a private
 * reply to the comment (D-082); the link has to ride on it.
 */
export function compileRecipe(recipe: QuickRecipe): GraphWrite {
  const nodes: GraphWrite['nodes'] = [];
  const edges: GraphWrite['edges'] = [];
  let previous: { key: string; branch: string } | null = null;

  /** Appends a node, wiring it to the previous one on that node's out-branch. */
  const append = (key: string, type: string, config: Record<string, unknown>, outBranch = 'default'): void => {
    if (previous) edges.push({ fromKey: previous.key, toKey: key, branch: previous.branch });
    nodes.push({ key, type, config, position: { x: 0, y: nodes.length * NODE_GAP } });
    previous = { key, branch: outBranch };
  };

  const channels = recipe.channelCode ? { channels: [recipe.channelCode] } : {};
  const filtersWords = recipe.match === 'specific_words' && recipe.keywords.length > 0;

  if (recipe.kind === 'dm') {
    // A DM keyword is a trigger of its own (indexed, D-023); "any message" is
    // the plain message trigger.
    append(
      'trigger',
      filtersWords ? 'keyword_received' : 'message_received',
      filtersWords ? { ...channels, keywords: recipe.keywords, matchMode: 'contains' } : channels,
    );
  } else {
    append('trigger', recipe.kind === 'comment' ? 'comment_received' : 'story_reply_received', { ...channels, ...triggerPin(recipe) });
    if (filtersWords) {
      // Only the Yes branch is wired: a comment or reply that does not match
      // simply ends the run, which is what "and this … has" means.
      append('keyword', 'message_contains_keyword', { keywords: recipe.keywords }, 'true');
    }
  }

  if (recipe.kind === 'comment' && recipe.publicReply.enabled && recipe.publicReply.text.trim()) {
    append('public-reply', 'reply_comment', { text: recipe.publicReply.text.trim() });
  }

  // A heart on their message first, as ManyChat does with story replies; on a
  // story with words it sits on the Yes branch like the DM.
  if (recipe.kind !== 'comment' && recipe.react) append('react', 'react_to_message', { reaction: 'love' });

  // Ask to follow before sending the link (D-106): followers get the DM on
  // Yes, everyone else the ask on No. In a DM or story reply the contact has
  // just written, so both answers are deliverable.
  const isGated = recipe.kind !== 'comment' && recipe.followGate.enabled && recipe.followGate.askText.trim() !== '';
  if (isGated) append('check-follow', 'follows_account', {}, 'true');

  const button = recipe.dm.button;
  const hasButton = button !== null && button.label.trim() !== '' && button.url.trim() !== '';
  append('dm', 'send_message', {
    text: recipe.dm.text.trim(),
    ...(hasButton ? { buttons: [{ id: 'open-link', label: button.label.trim(), url: button.url.trim() }] } : {}),
  });

  if (isGated) {
    const check = nodes.find((n) => n.key === 'check-follow')!;
    nodes.push({
      key: 'ask-follow',
      type: 'send_message',
      config: { text: recipe.followGate.askText.trim() },
      position: { x: 240, y: check.position.y + NODE_GAP },
    });
    edges.push({ fromKey: 'check-follow', toKey: 'ask-follow', branch: 'false' });
  }

  return { nodes, edges };
}

const KIND_OF_TRIGGER: Record<string, QuickKind> = {
  comment_received: 'comment',
  story_reply_received: 'story',
  keyword_received: 'dm',
  message_received: 'dm',
};

const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const strings = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []);

/** Node keys, types, configs and edges, without the layout: what makes two graphs the same automation. */
function graphShape(graph: Pick<Graph, 'nodes' | 'edges'> | GraphWrite): string {
  const nodes = [...graph.nodes]
    .map((n) => ({ key: n.key, type: n.type, label: n.label ?? null, config: n.config }))
    .sort((a, b) => a.key.localeCompare(b.key));
  const edges = graph.edges.map((e) => `${e.fromKey}>${e.toKey}:${e.branch ?? 'default'}`).sort();
  return JSON.stringify({ nodes, edges });
}

/**
 * Reads a quick automation's recipe back out of its graph, so it reopens in
 * the guided form rather than the canvas. The graph has to be exactly what
 * `compileRecipe` would write for that recipe (layout aside): one changed in
 * the builder — a step added, renamed or rewired — returns null and stays a
 * builder automation.
 */
export function recipeFromGraph(graph: Pick<Graph, 'nodes' | 'edges'>): QuickRecipe | null {
  const byKey = new Map(graph.nodes.map((n) => [n.key, n]));
  const trigger = byKey.get('trigger');
  const kind = trigger ? KIND_OF_TRIGGER[trigger.type] : undefined;
  const dm = byKey.get('dm');
  if (!trigger || !kind || !dm) return null;

  const base = emptyRecipe(kind);
  const config = trigger.config;
  const channelCode = strings(config['channels'])[0] ?? null;
  const keywords = kind === 'dm' ? strings(config['keywords']) : strings(byKey.get('keyword')?.config['keywords']);
  const postId = text(config['postId']) || null;
  const scheduledPostId = text(config['scheduledPostId']) || null;
  const storyId = text(config['storyId']) || null;
  const publicReply = byKey.get('public-reply');
  const ask = byKey.get('ask-follow');
  const button = Array.isArray(dm.config['buttons']) ? (dm.config['buttons'][0] as { label?: unknown; url?: unknown } | undefined) : undefined;

  const recipe: QuickRecipe = {
    ...base,
    channelCode,
    postScope: postId || scheduledPostId ? 'specific_post' : 'any_post',
    postId,
    scheduledPostId,
    storyScope: storyId ? 'specific_story' : 'any_story',
    storyId,
    match: keywords.length > 0 ? 'specific_words' : 'any_word',
    keywords,
    publicReply: publicReply ? { enabled: true, text: text(publicReply.config['text']) } : base.publicReply,
    dm: { text: text(dm.config['text']), button: button ? { label: text(button.label), url: text(button.url) } : null },
    react: byKey.has('react'),
    followGate: ask ? { enabled: true, askText: text(ask.config['text']) } : base.followGate,
  };
  return graphShape(compileRecipe(recipe)) === graphShape(graph) ? recipe : null;
}
